"use client"

import * as React from "react"
import { cn } from "cn"

import { prefersReducedMotion } from "../../lib/motion"

const COLUMNS_HALF = 18
const ROWS = 30
const Z_NEAR = 1.4
const Z_FAR = Z_NEAR + ROWS
const Z_SAMPLE_STEP = 0.5
const CAMERA_HEIGHT = 2.4
const HORIZON_RATIO = 0.34
const FOCAL_RATIO = 0.8
const WAVE_AMPLITUDE = 0.75
const LINE_WIDTH = 0.75
const LINE_ALPHA = 0.45
const SIGNAL_EVERY_MS = 1700
const MAX_SIGNALS = 4
const SIGNAL_TRAVEL_MS = { min: 5200, max: 7800 }
const SIGNAL_TRAIL = 2.2
const MAX_PIXEL_RATIO = 1.5

type Palette = { line: string; signal: string }
type Signal = { x: number; born: number; travel: number }

function readPalette(): Palette {
  const styles = getComputedStyle(document.documentElement)
  const dark = document.documentElement.classList.contains("dark")
  return {
    line: styles.getPropertyValue(dark ? "--color-greyscale-700" : "--color-greyscale-400").trim(),
    signal: styles.getPropertyValue("--color-primary-500").trim(),
  }
}

function surfaceHeight(x: number, z: number, t: number) {
  const swell = Math.sin(x * 0.32 + t * 0.00022) * Math.cos(z * 0.26 - t * 0.00017)
  const drift = Math.sin((x * 0.6 + z) * 0.14 + t * 0.00013)
  return WAVE_AMPLITUDE * (swell * 0.6 + drift * 0.4)
}

function LatticeField({ className }: { className?: string }) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null)

  React.useEffect(() => {
    const canvas = canvasRef.current
    const context = canvas?.getContext("2d")
    if (!canvas || !context) return

    let width = 0
    let height = 0
    let palette = readPalette()
    let signals: Signal[] = []
    let lastSignal = 0
    let frame = 0

    const resize = () => {
      const ratio = Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO)
      width = canvas.clientWidth
      height = canvas.clientHeight
      canvas.width = width * ratio
      canvas.height = height * ratio
      context.setTransform(ratio, 0, 0, ratio, 0, 0)
    }

    const draw = (t: number) => {
      const focal = height * FOCAL_RATIO
      const horizon = height * HORIZON_RATIO
      const cameraX = Math.sin(t * 0.00006) * 1.2
      const cameraY = CAMERA_HEIGHT + Math.sin(t * 0.00009) * 0.15
      const project = (x: number, z: number): [number, number] => [
        width / 2 + ((x - cameraX) * focal) / z,
        horizon + ((cameraY - surfaceHeight(x, z, t)) * focal) / z,
      ]

      context.globalCompositeOperation = "source-over"
      context.clearRect(0, 0, width, height)
      context.lineWidth = LINE_WIDTH
      context.strokeStyle = palette.line
      context.globalAlpha = LINE_ALPHA

      context.beginPath()
      for (let row = 0; row <= ROWS; row++) {
        const z = Z_NEAR + row
        for (let x = -COLUMNS_HALF; x <= COLUMNS_HALF; x++) {
          const [sx, sy] = project(x, z)
          if (x === -COLUMNS_HALF) context.moveTo(sx, sy)
          else context.lineTo(sx, sy)
        }
      }
      for (let x = -COLUMNS_HALF; x <= COLUMNS_HALF; x++) {
        for (let z = Z_NEAR; z <= Z_FAR; z += Z_SAMPLE_STEP) {
          const [sx, sy] = project(x, z)
          if (z === Z_NEAR) context.moveTo(sx, sy)
          else context.lineTo(sx, sy)
        }
      }
      context.stroke()

      context.strokeStyle = palette.signal
      context.fillStyle = palette.signal
      context.lineWidth = 1.25
      for (const signal of signals) {
        const progress = Math.min(1, (t - signal.born) / signal.travel)
        const head = Z_FAR - (Z_FAR - Z_NEAR - SIGNAL_TRAIL) * progress
        const fade = Math.sin(progress * Math.PI)
        context.globalAlpha = 0.9 * fade
        context.beginPath()
        for (let z = head; z <= head + SIGNAL_TRAIL; z += Z_SAMPLE_STEP / 2) {
          const [sx, sy] = project(signal.x, z)
          if (z === head) context.moveTo(sx, sy)
          else context.lineTo(sx, sy)
        }
        context.stroke()
        const [hx, hy] = project(signal.x, head)
        context.beginPath()
        context.arc(hx, hy, 1.8, 0, Math.PI * 2)
        context.fill()
      }

      const depthFade = context.createLinearGradient(0, horizon, 0, height)
      depthFade.addColorStop(0, "rgba(0,0,0,0)")
      depthFade.addColorStop(0.12, "rgba(0,0,0,0.35)")
      depthFade.addColorStop(0.45, "rgba(0,0,0,0.8)")
      depthFade.addColorStop(1, "rgba(0,0,0,1)")
      context.globalAlpha = 1
      context.globalCompositeOperation = "destination-in"
      context.fillStyle = depthFade
      context.fillRect(0, 0, width, height)
      context.globalCompositeOperation = "source-over"
    }

    const step = (now: number) => {
      if (now - lastSignal > SIGNAL_EVERY_MS && signals.length < MAX_SIGNALS) {
        const column = Math.round((Math.random() * 2 - 1) * COLUMNS_HALF * 0.8)
        signals.push({
          x: column,
          born: now,
          travel: SIGNAL_TRAVEL_MS.min + Math.random() * (SIGNAL_TRAVEL_MS.max - SIGNAL_TRAVEL_MS.min),
        })
        lastSignal = now
      }
      signals = signals.filter((signal) => now - signal.born < signal.travel)
      draw(now)
      frame = requestAnimationFrame(step)
    }

    const resizeObserver = new ResizeObserver(() => {
      resize()
      if (prefersReducedMotion()) draw(0)
    })
    const themeObserver = new MutationObserver(() => {
      palette = readPalette()
      if (prefersReducedMotion()) draw(0)
    })

    resize()
    resizeObserver.observe(canvas)
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] })
    if (prefersReducedMotion()) draw(0)
    else frame = requestAnimationFrame(step)

    return () => {
      cancelAnimationFrame(frame)
      resizeObserver.disconnect()
      themeObserver.disconnect()
    }
  }, [])

  return <canvas ref={canvasRef} aria-hidden="true" data-slot="lattice-field" className={cn("size-full", className)} />
}

export { LatticeField }
